import React, { useEffect, useMemo, useState } from 'react';

import { Container, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import dsarApi from 'config/api/dsar/dsar';
import type { DsarCaseAsset } from 'config/api/dsar/dsar.types';
import DIRECTIONS from 'config/constants/sortDirections';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';

import DsarCaseAssetsTable from './_components/DsarCaseAssetsTable';
import useStyles from './DsarCaseAssetsPage.styles';

const pageSize = 20;

const mapFilterItems = (items: { id: string; name: string }[]) =>
  items.sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));

const getSearchText = (caseAsset: DsarCaseAsset) =>
  [
    caseAsset.dsarCase.caseReference,
    caseAsset.asset.name,
    caseAsset.asset.owner.fullName,
    caseAsset.asset.owner.email,
    caseAsset.status.name,
    caseAsset.dataFound.name,
    ...caseAsset.requestTypeResults.map(result => result.requestType.name),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getSortValue = (caseAsset: DsarCaseAsset, key: string | null) => {
  switch (key) {
    case 'caseReference':
      return caseAsset.dsarCase.caseReference;
    case 'assetName':
      return caseAsset.asset.name;
    case 'owner':
      return caseAsset.asset.owner.fullName;
    case 'status':
      return caseAsset.status.name;
    case 'dataFound':
      return caseAsset.dataFound.name;
    case 'dueDate':
      return caseAsset.dueDate;
    case 'completedAt':
      return caseAsset.completedAt || '';
    default:
      return '';
  }
};

const buildDetailPath = (id: number) => PATHS.DSAR_CASE_ASSETS_DETAIL.replace(':id', String(id));

const DsarCaseAssetsPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { showHelpDialog } = useHelpDialogContext();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loadedCaseAssets, setLoadedCaseAssets] = useState<DsarCaseAsset[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, Array<{ id: string; name: string }>>>({});
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({
    key: null,
    direction: null,
  });

  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: currentOffset,
    }),
    [currentOffset],
  );

  const {
    data: queryData,
    isLoading,
    error,
  } = useQuery(
    ['DSAR_CASE_ASSETS_GET_ALL', queryParams],
    async () => {
      const { data } = await dsarApi.getAllCaseAssets(queryParams);
      return data;
    },
    {
      keepPreviousData: true,
      refetchInterval: 5000,
    },
  );

  useEffect(() => {
    if (!queryData) return;
    setLoadedCaseAssets(previousAssets => {
      if (currentOffset === 0) return queryData.results;
      const existingIds = new Set(previousAssets.map(caseAsset => caseAsset.id));
      return [...previousAssets, ...queryData.results.filter(caseAsset => !existingIds.has(caseAsset.id))];
    });
  }, [currentOffset, queryData]);

  const availableFilters = useMemo(() => {
    const statusMap = new Map<string, { id: string; name: string }>();
    const dataFoundMap = new Map<string, { id: string; name: string }>();
    const requestTypeMap = new Map<string, { id: string; name: string }>();

    loadedCaseAssets.forEach(caseAsset => {
      statusMap.set(caseAsset.status.code, { id: caseAsset.status.code, name: caseAsset.status.name });
      dataFoundMap.set(caseAsset.dataFound.code, { id: caseAsset.dataFound.code, name: caseAsset.dataFound.name });
      caseAsset.requestTypeResults.forEach(result => {
        requestTypeMap.set(result.requestType.code, {
          id: result.requestType.code,
          name: result.requestType.name,
        });
      });
    });

    return {
      status: {
        sectionName: dsar_messages.case_assets.filters.status,
        filters: mapFilterItems(Array.from(statusMap.values())),
      },
      data_found: {
        sectionName: dsar_messages.case_assets.filters.data_found,
        filters: mapFilterItems(Array.from(dataFoundMap.values())),
      },
      request_types: {
        sectionName: dsar_messages.case_assets.filters.request_types,
        filters: mapFilterItems(Array.from(requestTypeMap.values())),
      },
    };
  }, [loadedCaseAssets]);

  const tableData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const statusFilterIds = new Set((filters.status || []).map(filter => filter.id));
    const dataFoundFilterIds = new Set((filters.data_found || []).map(filter => filter.id));
    const requestTypeFilterIds = new Set((filters.request_types || []).map(filter => filter.id));

    const filteredAssets = normalizedSearch
      ? loadedCaseAssets.filter(caseAsset => getSearchText(caseAsset).includes(normalizedSearch))
      : loadedCaseAssets;

    const filterMatchedAssets = filteredAssets.filter(caseAsset => {
      const matchesStatus = !statusFilterIds.size || statusFilterIds.has(caseAsset.status.code);
      const matchesDataFound = !dataFoundFilterIds.size || dataFoundFilterIds.has(caseAsset.dataFound.code);
      const matchesRequestTypes =
        !requestTypeFilterIds.size || caseAsset.requestTypeResults.some(result => requestTypeFilterIds.has(result.requestType.code));

      return matchesStatus && matchesDataFound && matchesRequestTypes;
    });

    if (!sort.key || !sort.direction) return filterMatchedAssets;

    return [...filterMatchedAssets].sort((left, right) => {
      const leftValue = String(getSortValue(left, sort.key)).toLowerCase();
      const rightValue = String(getSortValue(right, sort.key)).toLowerCase();
      const result = leftValue.localeCompare(rightValue);
      return sort.direction === DIRECTIONS.DESC ? -result : result;
    });
  }, [filters, loadedCaseAssets, searchTerm, sort]);

  const handleOpen = React.useCallback(
    (caseAsset: DsarCaseAsset) => {
      history.push(buildDetailPath(caseAsset.id));
    },
    [history],
  );

  const handleEdit = React.useCallback(
    (caseAsset: DsarCaseAsset) => {
      history.push(buildDetailPath(caseAsset.id), { openEdit: true });
    },
    [history],
  );

  const handleShowMore = React.useCallback(() => {
    setCurrentOffset(previousOffset => previousOffset + pageSize);
  }, []);

  const handleSort = React.useCallback((key: string | null, direction: string | null) => {
    setSort({ key, direction });
  }, []);

  const openHelpDialog = React.useCallback(() => {
    showHelpDialog({
      title: t(dsar_messages.case_assets.help.title),
      body: t(dsar_messages.case_assets.help.body),
    });
  }, [showHelpDialog, t]);

  let content: React.ReactNode;
  if (error) {
    content = <Typography color='error'>{t(general_messages.something_went_wrong)}</Typography>;
  } else if (isLoading && !queryData) {
    content = <Loader inner />;
  } else {
    content = (
      <>
        <DsarCaseAssetsTable
          data={tableData}
          filtersService={{ availableFilters, filters, setFilters }}
          loading={isLoading}
          onEdit={handleEdit}
          onOpen={handleOpen}
          onSearch={setSearchTerm}
          onSort={handleSort}
          searchValue={searchTerm}
          sort={sort}
        />
        {tableData.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            <Typography variant='h6'>{t(dsar_messages.case_assets.empty_state.title)}</Typography>
            <Typography color='textSecondary'>{t(dsar_messages.case_assets.empty_state.description)}</Typography>
          </div>
        )}
        {queryData?.next && (
          <FooterButton className='' loading={isLoading} onClick={handleShowMore}>
            {t(general_messages.show_more)}
          </FooterButton>
        )}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <TextButton aria-label={t(general_messages.how_it_works)} onClick={openHelpDialog}>
              {t(general_messages.how_it_works)}
            </TextButton>
          }
        >
          {t(dsar_messages.case_assets.page_title)}
        </ManagePageHeader>
        <Typography className={styles.pageDescription} color='textSecondary'>
          {t(dsar_messages.case_assets.page_description)}
        </Typography>
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default DsarCaseAssetsPage;
